// Jobs: the list by stage, with search and a stage filter. Rows open the job card.
import { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { Page } from "../shell/Page";
import { ListMore, useNewestList } from "../components/newest-list";
import { useT } from "../lib/i18n";
import { useNow, type Job } from "../hooks/slice-a";
import { SIDE_STAGES, STAGES, aud, day, m2 } from "../lib/format";
import { Pill, StagePill, Tabs, daysBetween, inputCls, selectCls } from "../components/job-bits";

const ALL_STAGES = [...STAGES, ...SIDE_STAGES];

export function Jobs() {
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("");
  useEffect(() => { const t = setTimeout(() => setQ(text.trim()), 250); return () => clearTimeout(t); }, [text]);
  const [params,setParams]=useSearchParams();
  const group=params.get('group')??'jobs';
  // Newest created first, one page at a time; tab, stage and search are decided by the API over the whole register.
  const list=useNewestList<Job&{register_group:string},{counts?:Record<string,number>;stages?:Record<string,{n:number;total:number}>}>("/api/job-register",{group,stage:stage||undefined,q:q||undefined});
  const jobs=list.query,rows=list.rows,counts=list.first?.counts,stages=list.first?.stages??{};
  const now = useNow(60_000);
  const known=(s:string|null|undefined)=>ALL_STAGES.includes(s as (typeof ALL_STAGES)[number]);
  const stageTotals=(s:string)=>s==="No stage"?Object.entries(stages).filter(([k])=>!known(k)).reduce((n,[,v])=>({n:n.n+v.n,total:n.total+v.total}),{n:0,total:0}):stages[s]??{n:0,total:0};
  const groups = (stage ? [stage] : [...ALL_STAGES, "No stage"]).map((s) => ({ stage: s, rows: rows.filter((j) => s === "No stage" && !stage ? !known(j.effective_stage) : (j.effective_stage ?? "No stage") === s), ...stageTotals(s) })).filter((g) => g.n > 0);

  if(group==='enquiries')return <Navigate to="/pipeline" replace/>;
  return (
    <Page title="Jobs" lede="Accepted work and delivery history. Unaccepted quotations are in Pipeline." actions={<span className="num text-micro text-muted-foreground">{jobs.isSuccess?`${list.total} records`:jobs.isError?"Register unavailable":"Loading register…"}</span>}>
      <Tabs value={group} onChange={g=>{setParams({group:g});setStage("")}} items={[{id:'jobs',label:'Delivery jobs',n:counts?.jobs},{id:'history',label:'Stalled & lost history',n:counts?.history},{id:'review',label:'Needs review',n:counts?.review}]}/>
      <p className="mb-3 text-sm text-muted-foreground">{group==='jobs'?'These records have a delivery or invoicing stage. Historical stage labels are not independent proof of customer acceptance. New jobs require an accepted offer.':group==='history'?'Stalled or lost history does not establish whether a customer previously accepted. These records are preserved without counting them as current enquiries or delivery jobs.':'Missing or conflicting lifecycle information needs review before classification.'} <Link className="text-primary underline" to="/pipeline">Open Pipeline</Link></p>
      {jobs.isError&&<p role="alert" className="mb-3 text-destructive">The register could not be loaded completely. Refresh before relying on counts.</p>}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <label className="relative w-full max-w-sm">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <input className={`${inputCls} pl-8`} placeholder="Raman, J0926…, Epping" value={text} onChange={(e) => setText(e.target.value)} aria-label="Search jobs" />
        </label>
        <select className={selectCls} value={stage} onChange={(e) => setStage(e.target.value)} aria-label="Stage"><option value="">Every stage</option>{[...new Set([...Object.keys(stages),...(stage?[stage]:[])])].map((s) => <option key={s}>{s}</option>)}</select>
      </div>

      <div className="overflow-hidden rounded-lg border bg-card">
        <table className="w-full text-sm">
          <thead className="text-left font-mono text-micro uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-medium">Ref</th>
              <th className="px-3 py-2 font-medium">Job</th>
              <th className="hidden px-3 py-2 font-medium md:table-cell">Product · area</th>
              <th className="hidden px-3 py-2 font-medium sm:table-cell">Next</th>
              <th className="px-3 py-2 text-right font-medium">Value</th>
              <th className="hidden px-3 py-2 text-right font-medium md:table-cell">Age</th>
            </tr>
          </thead>
          <tbody>
            {jobs.isPending && <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">Loading jobs…</td></tr>}
            {jobs.isSuccess && list.total === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">No records match.</td></tr>}
            {groups.map((g) => (
              <GroupRows key={g.stage} stage={g.stage} rows={g.rows} n={g.n} total={g.total} now={now} onOnly={stage || g.stage === "No stage" ? undefined : () => setStage(g.stage)} />
            ))}
          </tbody>
        </table>
      </div>
      <ListMore list={list} />
    </Page>
  );
}

/** One stage of the register. `n` and `total` are the stage's whole count and value from the API; `rows` are the newest loaded so far. */
function GroupRows({ stage, rows, n, total, now, onOnly }: { stage: string; rows: Job[]; n: number; total: number; now: number; onOnly?: () => void }) {
  const { t } = useT();
  return (
    <>
      <tr className="border-t bg-muted/40">
        <td colSpan={4} className="px-3 py-1.5"><span className="inline-flex flex-wrap items-center gap-2"><StagePill stage={stage} /><span className="font-mono text-micro text-muted-foreground">{n}</span>
          {rows.length < n && <span className="text-micro text-muted-foreground">{t('list.stageShowing', { shown: String(rows.length), total: String(n) })}{onOnly && <> · <button type="button" className="text-primary underline" onClick={onOnly}>{t('list.onlyStage')}</button></>}</span>}</span></td>
        <td className="num px-3 py-1.5 text-right text-micro text-gold">{total > 0 ? aud(total) : ""}</td>
        <td className="hidden md:table-cell" />
      </tr>
      {rows.map((j) => {
        const dup = j.workflow_flags?.possible_duplicate;
        const next = j.install_next_date ? `install ${day(j.install_next_date)}` : j.close_date ? `closed ${day(j.close_date)}` : j.tags?.[0] ?? "";
        return (
          <tr key={j.ref} className="border-t hover:bg-muted/50">
            <td className="px-3 py-2 font-mono text-micro"><Link className="text-primary" to={`/jobs/${j.ref}`}>{j.ref}</Link></td>
            <td className="px-3 py-2">
              <Link className="font-medium hover:underline" to={`/jobs/${j.ref}`}>{j.name ?? j.ref}</Link>
              {dup ? <Pill tone="warn" className="ml-1.5">possible duplicate</Pill> : null}
              <div className="text-micro text-muted-foreground">{j.suburb ?? "—"}{j.address ? ` · ${j.address}` : ""}</div>
            </td>
            <td className="hidden px-3 py-2 md:table-cell"><div className="truncate">{j.product ?? <span className="text-muted-foreground">product to confirm</span>}</div><div className="num text-micro text-muted-foreground">{j.area_m2 ? m2(j.area_m2) : ""}{j.stairs ? ` · ${j.stairs} stairs` : ""}</div></td>
            <td className="hidden px-3 py-2 text-micro text-muted-foreground sm:table-cell">{next}</td>
            <td className="num px-3 py-2 text-right text-gold">{j.deal_value != null ? aud(j.deal_value) : <span className="text-muted-foreground">—</span>}</td>
            <td className="num hidden px-3 py-2 text-right text-micro text-muted-foreground md:table-cell">{daysBetween(j.creation_date ?? j.created_at, now)} d</td>
          </tr>
        );
      })}
    </>
  );
}
